import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpenText,
  Coins,
  Home,
  LogOut,
  LucideIcon,
  Menu as MenuIcon,
  Settings2,
  ShoppingBag,
  X,
} from "lucide-react";
import { getDownloadURL, ref } from "firebase/storage";
import yourLogo from "../assets/twoje-logo.png";
import { signOut, storage } from "../firebase";
import useCtx from "../hooks/useCts";

const FALLBACK_LOGO = yourLogo;
const logoSrcCache = new Map<string, string>();
const logoWidthCache = new Map<string, number>();
const LOGO_PERSIST_KEY_PREFIX = "top-nav-logo:";
const DEFAULT_LOGO_SLOT_WIDTH = 140;
const MIN_LOGO_SLOT_WIDTH = 40;
const MAX_LOGO_SLOT_WIDTH = 140;
const TARGET_LOGO_HEIGHT = 40;

const navigationItems = [
  { to: "/", label: "HOME", Icon: Home },
  { to: "/zamowienia", label: "ZAMÓWIENIA", Icon: ShoppingBag },
  { to: "/zarzadzanie", label: "ADMIN", Icon: Settings2 },
  { to: "/podreczniki", label: "PODRĘCZNIKI", Icon: BookOpenText },
  { to: "/program-lojalnosciowy", label: "PUNKTY", Icon: Coins },
] as const;

const isDirectLogoUrl = (value?: string | null) =>
  Boolean(value && /^https?:\/\//i.test(value));

const isActiveRoute = (pathname: string, to: string) =>
  to === "/" ? pathname === "/" : pathname.startsWith(to);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

type PersistedLogoState = {
  logoUrl: string;
  src: string;
  width: number;
};

const readPersistedLogoState = (
  clientId?: string | null,
): PersistedLogoState | null => {
  if (!clientId || typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(`${LOGO_PERSIST_KEY_PREFIX}${clientId}`);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PersistedLogoState>;
    if (
      typeof parsed.logoUrl !== "string" ||
      typeof parsed.src !== "string" ||
      typeof parsed.width !== "number"
    ) {
      return null;
    }

    return {
      logoUrl: parsed.logoUrl,
      src: parsed.src,
      width: parsed.width,
    };
  } catch {
    return null;
  }
};

const writePersistedLogoState = (
  clientId: string,
  state: PersistedLogoState,
) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      `${LOGO_PERSIST_KEY_PREFIX}${clientId}`,
      JSON.stringify(state),
    );
  } catch {
    // Ignore storage write failures and fall back to in-memory caches.
  }
};

interface NavItemProps {
  to: string;
  label: string;
  Icon: LucideIcon;
  pathname: string;
  isMobile?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  label,
  Icon,
  pathname,
  isMobile = false,
}) => {
  const isActive = isActiveRoute(pathname, to);

  return (
    <Link
      to={to}
      aria-current={isActive ? "page" : undefined}
      className={`group inline-flex items-center gap-2.5 transition-all duration-300 ${
        isMobile
          ? "w-full justify-start px-1 py-3"
          : "min-h-[44px] min-w-0 px-2 py-2"
      } ${isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
    >
      <Icon
        size={isMobile ? 18 : 16}
        strokeWidth={isActive ? 2.4 : 2.1}
        aria-hidden="true"
        className={`shrink-0 transition-transform duration-300 ${
          isActive ? "" : "group-hover:-translate-y-0.5"
        }`}
      />

      <span className="min-w-0">
        <span
          className={`block truncate font-semibold tracking-[0.08em] ${
            isMobile ? "text-sm" : "text-[11px]"
          }`}
        >
          {label}
        </span>
      </span>
    </Link>
  );
};

interface Props {
  hideLinks?: boolean;
}

const TopNavigator: React.FC<Props> = ({ hideLinks = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = useCtx();

  const currentClient = ctx.currentClient;

  const persistedLogoState = useMemo(
    () => readPersistedLogoState(currentClient?.id ?? null),
    [currentClient?.id],
  );

  const initialLogoSrc = useMemo(() => {
    if (!currentClient) return FALLBACK_LOGO;

    if (
      persistedLogoState &&
      persistedLogoState.logoUrl === currentClient.logoUrl
    ) {
      return persistedLogoState.src;
    }

    if (currentClient.logoUrl) {
      if (isDirectLogoUrl(currentClient.logoUrl)) {
        return currentClient.logoUrl;
      }

      return logoSrcCache.get(currentClient.id) ?? FALLBACK_LOGO;
    }

    return FALLBACK_LOGO;
  }, [currentClient, persistedLogoState]);

  const initialLogoSlotWidth = useMemo(() => {
    if (!currentClient) return DEFAULT_LOGO_SLOT_WIDTH;

    if (
      persistedLogoState &&
      persistedLogoState.logoUrl === currentClient.logoUrl
    ) {
      return persistedLogoState.width;
    }

    return logoWidthCache.get(currentClient.id) ?? DEFAULT_LOGO_SLOT_WIDTH;
  }, [currentClient, persistedLogoState]);

  const [logoSrc, setLogoSrc] = useState(initialLogoSrc);
  const [logoSlotWidth, setLogoSlotWidth] = useState(initialLogoSlotWidth);
  const showNavigation =
    !hideLinks &&
    ctx.isRoleResolved &&
    Boolean(ctx.currentClient) &&
    !ctx.isTeacher;
  const showActions = !hideLinks;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen || !showNavigation) return;

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDownOutside);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutside);
    };
  }, [mobileMenuOpen, showNavigation]);

  useEffect(() => {
    setLogoSrc(initialLogoSrc);
  }, [initialLogoSrc]);

  useEffect(() => {
    setLogoSlotWidth(initialLogoSlotWidth);
  }, [initialLogoSlotWidth]);

  useEffect(() => {
    let isMounted = true;

    const loadLogo = async () => {
      try {
        const clientData = currentClient;
        if (!clientData) {
          if (isMounted) setLogoSrc(FALLBACK_LOGO);
          return;
        }

        if (!clientData.logoUrl) {
          if (isMounted) setLogoSrc(FALLBACK_LOGO);
          return;
        }

        if (isDirectLogoUrl(clientData.logoUrl)) {
          logoSrcCache.set(clientData.id, clientData.logoUrl);
          if (isMounted) setLogoSrc(clientData.logoUrl);
          return;
        }

        const cachedLogo = logoSrcCache.get(clientData.id);
        if (cachedLogo && isMounted) {
          setLogoSrc(cachedLogo);
        }

        const url = await getDownloadURL(ref(storage, clientData.logoUrl));
        logoSrcCache.set(clientData.id, url);
        if (isMounted) setLogoSrc(url);
      } catch (error) {
        console.error("Nie udało się pobrać logo:", error);
        if (isMounted) setLogoSrc(FALLBACK_LOGO);
      }
    };

    void loadLogo();

    return () => {
      isMounted = false;
    };
  }, [currentClient]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Błąd wylogowania:", error);
    }
  };

  const handleLogoLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalHeight, naturalWidth } = event.currentTarget;

    if (!naturalHeight || !naturalWidth) return;

    const fittedWidth = clamp(
      Math.round((naturalWidth / naturalHeight) * TARGET_LOGO_HEIGHT),
      MIN_LOGO_SLOT_WIDTH,
      MAX_LOGO_SLOT_WIDTH,
    );

    if (currentClient) {
      logoWidthCache.set(currentClient.id, fittedWidth);
      if (currentClient.logoUrl) {
        writePersistedLogoState(currentClient.id, {
          logoUrl: currentClient.logoUrl,
          src: event.currentTarget.currentSrc || event.currentTarget.src,
          width: fittedWidth,
        });
      }
    }

    setLogoSlotWidth(fittedWidth);
  };

  const logoWidthStyle = {
    width: `min(${logoSlotWidth}px, calc(100vw - 10rem))`,
  };

  return (
    <header className="relative z-40 px-3 pt-3 sm:px-5 sm:pt-4 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div ref={mobileMenuRef} className="relative">
          <nav className="flex w-full gap-2 rounded-[38px]  px-4 py-3 border border-white/50 bg-white/20 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm transition-all duration-300">
            <div className="min-w-0 shrink-0 items-center flex px-2">
              <Link
                to="/"
                className="group inline-flex h-8 items-center justify-start transition duration-300 hover:opacity-80 sm:h-10"
                aria-label="Przejdź do strony głównej"
                style={logoWidthStyle}
              >
                <img
                  src={logoSrc}
                  alt="Logo"
                  onLoad={handleLogoLoad}
                  className="block max-h-full w-full object-contain object-left"
                />
              </Link>
            </div>

            <div className="hidden min-w-0 flex-1 items-center justify-start overflow-hidden lg:flex">
              {showNavigation && (
                <div className="flex min-w-0 items-center gap-4 xl:gap-5">
                  {navigationItems.map(({ to, label, Icon }) => (
                    <NavItem
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      pathname={location.pathname}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="ml-auto flex shrink-0 items-center justify-end gap-1">
              {showNavigation && (
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  aria-label={mobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                  aria-controls="top-navigation-mobile"
                  aria-expanded={mobileMenuOpen}
                  className="inline-flex h-10 w-10 items-center justify-center text-slate-700 transition hover:text-slate-950 lg:hidden"
                >
                  {mobileMenuOpen ? (
                    <X size={18} strokeWidth={2.3} aria-hidden="true" />
                  ) : (
                    <MenuIcon size={18} strokeWidth={2.3} aria-hidden="true" />
                  )}
                </button>
              )}

              {showActions ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden h-10 w-10 items-center justify-center text-slate-700 transition hover:text-rose-600 lg:inline-flex"
                  aria-label="Wyloguj"
                  title="Wyloguj się"
                >
                  <LogOut size={16} strokeWidth={2.2} aria-hidden="true" />
                </button>
              ) : (
                <div className="h-10 w-10 shrink-0" aria-hidden="true" />
              )}
            </div>
          </nav>

          {mobileMenuOpen && showNavigation && (
            <div
              id="top-navigation-mobile"
              className="absolute inset-x-0 top-[calc(100%+0.75rem)] z-20 lg:hidden"
            >
              <div className="rounded-[32px] border border-white/70 bg-white/65 p-3 shadow-lg backdrop-blur-lg">
                <div className="grid gap-1">
                  {navigationItems.map(({ to, label, Icon }) => (
                    <NavItem
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      pathname={location.pathname}
                      isMobile
                    />
                  ))}
                  {showActions ? (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="inline-flex w-full items-center gap-2.5 px-1 py-3 text-left text-slate-500 transition hover:text-rose-600"
                    >
                      <LogOut
                        size={18}
                        strokeWidth={2.1}
                        aria-hidden="true"
                        className="shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold tracking-[0.08em]">
                          WYLOGUJ
                        </span>
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopNavigator;
